import { type } from "arktype";
import { Adaptation, ChannelKind, GoalType, Horizon, Role, Tier, Unit } from "./vocabulary";

export const Practice = type({
  id: "string",
  tenantId: "string",
  name: "string",
  country: "string",
  units: Unit,
  freePeriodEndsAt: "string",
  tier: Tier,
  "branding?": { "logoUrl?": "string", "color?": "string" },
  createdAt: "string",
});
export type Practice = typeof Practice.infer;

export const User = type({ id: "string", email: "string", name: "string", "hubUserId?": "string", createdAt: "string" });
export type User = typeof User.infer;

export const Membership = type({ id: "string", userId: "string", practiceId: "string", role: Role, "hubPrincipalId?": "string", createdAt: "string" });
export type Membership = typeof Membership.infer;

export const ReachChannel = type({
  id: "string",
  kind: ChannelKind,
  address: "string",
  primary: "boolean",
  "optedOutAt?": "string | null",
});
export type ReachChannel = typeof ReachChannel.infer;

// R-INT-02: consent is recorded with timestamp and the exact text consented to.
export const Consent = type({
  id: "string",
  channelId: "string",
  consentedAt: "string",
  text: "string",
  "revokedAt?": "string | null",
});
export type Consent = typeof Consent.infer;

// R-INT-03: goals are superseded, never overwritten.
export const Goal = type({
  id: "string",
  clientId: "string",
  verbatim: "string",
  normalized: "string",
  goalType: GoalType,
  horizon: Horizon,
  "target?": { metric: "string", value: "number", unit: "string" },
  priority: "number",
  setAt: "string",
  "supersedesId?": "string | null",
  status: "'active' | 'superseded' | 'achieved' | 'retired'",
  "checkInCadenceDays?": "number",
});
export type Goal = typeof Goal.infer;

// R-INT-04: the trainer's own hypothesis, labeled as such.
export const Strategy = type({
  id: "string",
  goalId: "string",
  intent: "string",
  principles: "string[]",
  "explicitRepetition?": "boolean",
  setAt: "string",
});
export type Strategy = typeof Strategy.infer;

export const GoalCheckIn = type({
  id: "string",
  goalId: "string",
  dueAt: "string",
  "completedAt?": "string | null",
  "clientVerbatim?": "string",
  "measured?": { metric: "string", value: "number", unit: "string" },
  "trainerAssessment?": "string",
  "changes?": "string",
});
export type GoalCheckIn = typeof GoalCheckIn.infer;

export const InjuryStatus = type({
  answered: "boolean",
  "none?": "boolean",
  "reports?": type({ location: "string", "description?": "string", kind: "'discomfort' | 'pain' | 'unclassified'", reportedAt: "string", "resolvedAt?": "string | null" }).array(),
});
export type InjuryStatus = typeof InjuryStatus.infer;

// R-INT-05
export const Baseline = type({
  "trainingHistory?": "string",
  injuryStatus: InjuryStatus,
  "medicalFlags?": "string[]",
  "equipment?": "string[]",
  "schedule?": { "sessionsPerWeek?": "number", "days?": "string[]" },
  "preferences?": "string[]",
  "dislikes?": "string[]",
  "experience?": type({ pattern: "string", level: "'novice' | 'intermediate' | 'advanced'" }).array(),
  "nutritionFlags?": "string[]",
});
export type Baseline = typeof Baseline.infer;

// R-INT-06: tested vs estimated at the data level.
export const Assessment = type({
  id: "string",
  clientId: "string",
  name: "string",
  method: "string",
  value: "number",
  unit: "string",
  tested: "boolean",
  "exerciseId?": "string",
  "notes?": "string",
  effectiveAt: "string",
  observedAt: "string",
});
export type Assessment = typeof Assessment.infer;

export const Client = type({
  id: "string",
  practiceId: "string",
  name: "string",
  "userId?": "string | null",
  status: "'active' | 'archived' | 'read_only'",
  "sample?": "boolean",
  "unitsOverride?": Unit,
  channels: ReachChannel.array(),
  consents: Consent.array(),
  "baseline?": Baseline,
  "frequencyPerWeek?": "number",
  "confirmedAdaptations?": Adaptation.array(),
  createdAt: "string",
});
export type Client = typeof Client.infer;
